import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { Lock, User } from "lucide-react";
import { BusinessDetailsForm } from "@/design/BusinessDetailsForm";
import { DataUnavailable } from "@/design/DataUnavailable";
import { Banner, Card, Field, StepIndicator } from "@/design/ui";
import { formStateFor as authStateFor } from "@/modules/identity/commands/authCopy";
import { formStateFor as onboardingStateFor } from "@/modules/onboarding/commands/onboardingCopy";
import { formatLebanesePhone } from "@/modules/identity/surfaces";
import type { Category } from "@/modules/catalog/surfaces";
import { useMe } from "~/auth/MeProvider";
import { adoptSession } from "~/auth/supabase";
import { AuthShell } from "~/components/AuthShell";
import { FootLink, SubmitButton } from "~/components/forms";
import { ScreenPending } from "~/components/ScreenPending";
import { post } from "~/lib/api";
import { apiFormAction } from "~/screens/business/SettingsToolScreens";
import { useForm } from "~/lib/useForm";
import { useQuery } from "~/lib/useQuery";

/**
 * Registering a clinic, in two screens (R22 T14).
 *
 * `/join` creates the **account** and `/join/business` describes the
 * **clinic**. They are separate because the account has to exist and be proved
 * before there is anybody to attach a registration to, and because H37 lets an
 * existing member run only the second one.
 *
 * Neither is behind the business guard: the whole point is that the caller has
 * no clinic yet.
 */

// ── The account (docs/ux/screens/JoinAccountScreen.md) ───────────────────────

interface MemberSignUpResult {
  phone: string;
  verificationRequired: boolean;
  session: { accessToken: string; refreshToken: string };
}

/**
 * **The session arrives masked and is adopted anyway** (H47). Verifying unmasks
 * the very session handed over here, so discarding it would leave nothing for
 * the code to unmask and no way back in. It resolves as no caller until then,
 * which is what lets the verification screen's guest-only guard admit them.
 */
export function JoinAccountScreen() {
  const navigate = useNavigate();
  const { me, reload } = useMe();
  const { state, pending, submit } = useForm();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // An unaffiliated member has an account already and needs the clinic form; a
  // member with a clinic may register another (H37) and goes there too.
  if (me?.kind === "unaffiliated" || me?.kind === "member") {
    return <Navigate to="/join/business" replace />;
  }

  // A Consumer or an admin is an explained dead end, not a 404: one role per
  // account is a rule they can act on, and the action is to log out.
  if (me && me.kind !== "guest") {
    return <WrongAccountKind kind={me.kind} />;
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const created = await submit<MemberSignUpResult>(
      () =>
        post("/auth/staff/signup", {
          firstName,
          lastName,
          phone,
          password,
          confirmPassword,
        }),
      authStateFor,
      { firstName, lastName, phone },
    );
    if (!created) return;

    await adoptSession(created.session);
    await reload();
    const query = new URLSearchParams({
      phone: created.phone,
      role: "member",
    });
    void navigate(`/verify?${query.toString()}`);
  }

  return (
    <AuthShell>
      <div className="mb-6">
        <StepIndicator current={1} total={2} />
        <h1 className="mt-4 text-2xl font-extrabold text-ink">
          Register your business
        </h1>
        <p className="mt-1 text-sm text-muted">
          First your account, then your clinic&apos;s details
        </p>
      </div>

      {state.formError && <Banner>{state.formError}</Banner>}

      <form onSubmit={onSubmit}>
        <fieldset disabled={pending} className="disabled:opacity-70">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="First name"
              name="firstName"
              value={firstName}
              onChange={setFirstName}
              error={state.fieldErrors?.firstName}
              autoComplete="given-name"
              icon={User}
            />
            <Field
              label="Last name"
              name="lastName"
              value={lastName}
              onChange={setLastName}
              error={state.fieldErrors?.lastName}
              autoComplete="family-name"
              icon={User}
            />
          </div>
          {/* H47: the business side is identified by phone, exactly as the
              consumer side is, and the number is proved by the same WhatsApp
              OTP before this account reaches a single business screen. */}
          <Field
            label="Phone number"
            name="phone"
            type="tel"
            inputMode="tel"
            placeholder="71 234 456"
            prefix="+961"
            value={phone}
            onChange={(v) => setPhone(formatLebanesePhone(v))}
            error={state.fieldErrors?.phone}
            autoComplete="tel"
          />
          <Field
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={setPassword}
            help="At least 8 characters"
            error={state.fieldErrors?.password}
            autoComplete="new-password"
            icon={Lock}
          />
          <Field
            label="Confirm password"
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            error={state.fieldErrors?.confirmPassword}
            autoComplete="new-password"
            icon={Lock}
          />
          <SubmitButton pending={pending} pendingLabel="Creating…">
            Continue
          </SubmitButton>
        </fieldset>
      </form>

      <FootLink to="/login/staff" className="mt-6">
        I already have an account
      </FootLink>
      <FootLink lead="Looking to book?" to="/signup">
        Sign up as a customer
      </FootLink>
    </AuthShell>
  );
}

/** One role per account, said plainly, with the one action that helps. */
function WrongAccountKind({ kind }: { kind: "consumer" | "admin" }) {
  const { signOut } = useMe();
  const navigate = useNavigate();
  const roleLabel = kind === "admin" ? "an admin" : "a customer";

  return (
    <AuthShell>
      <h1 className="mb-3 text-xl font-bold text-ink">Register your business</h1>
      <p className="mb-6 text-sm text-body">
        This account is {roleLabel} account. To register a business, log out and
        create a business account.
      </p>
      <button
        type="button"
        className="text-sm font-medium text-muted hover:text-ink"
        onClick={() => {
          void signOut().then(() => navigate("/", { replace: true }));
        }}
      >
        Log out
      </button>
    </AuthShell>
  );
}

// ── The clinic (docs/ux/screens/BusinessDetailsScreen.md) ────────────────────

interface RegistrationPayload {
  registration: { businessName: string; submittedAt: string } | null;
  categories: Category[];
}

/**
 * **One active registration per account**, so an account that already has one
 * waiting is sent to its status rather than shown the form again. The server
 * answers that question with the form's data, because the screen branches on it
 * before it renders anything.
 *
 * H37 is why a member with a clinic reaches this at all: registering another is
 * allowed, and it is the *registration* that stays one per account, enforced by
 * a unique constraint rather than by turning members away at the door.
 */
export function BusinessDetailsScreen() {
  const navigate = useNavigate();
  const registration = useQuery<RegistrationPayload>("/registration");

  if (registration.data?.registration) {
    return <Navigate to="/business" replace />;
  }

  return (
    <AuthShell>
      {registration.loading && !registration.data ? (
        <ScreenPending />
      ) : registration.failure || !registration.data ? (
        <Card>
          <DataUnavailable
            configured={registration.failure !== "not_configured"}
            what="your business details"
          />
        </Card>
      ) : (
        <BusinessDetailsForm
          categories={registration.data.categories}
          submit={apiFormAction({
            method: "POST",
            path: () => "/registration",
            render: onboardingStateFor,
            // Straight to the status screen: there is nothing else to do here,
            // and the wait is what the person now needs to see.
            onSuccess: () => navigate("/business", { replace: true }),
          })}
        />
      )}
    </AuthShell>
  );
}
